/**
 * The match-pool tier of one member on the admin create-match screen
 * (/matches/new), as one line under the UUID field.
 *
 * WHY THIS COMPONENT EXISTS. An admin-created match deliberately ignores the
 * match-pool tier — the tier is a scheduling control on the engine, and a
 * named, permissioned, audited human act is the opposite of an engine pick
 * (the argument is in the header of backend/src/services/admin-match-create.ts).
 * That exemption rests on the act being INFORMED, and the screen showed two
 * raw UUIDs and no tier, so an operator could not see a 'dormant' member while
 * deciding.
 *
 * 🔴 It DISCLOSES and never BLOCKS. The create button must stay enabled for
 * every tier; a block here would re-create the hold that exemption rejects.
 * That is why a non-active tier also prints the "ignores the tier on purpose"
 * line: the operator should not read a warning as a refusal.
 *
 * The sentence comes from describeMatchPoolTier, the SAME helper the user page
 * uses, so one member reads identically in both places.
 *
 * 🔴 AND IT SAYS WHETHER THE CREATE WILL WORK (2026-10-30). The tier was the
 * only thing this line disclosed, and the tier line always ends with "it does
 * not block the create" - true, and no answer at all to the question the
 * operator is actually asking. adminCreateMatch runs the canonical eligibility
 * predicate on both members (constraint 2 of 7) and REFUSES a member who fails
 * it. So the screen printed the most reassuring thing it had, "Active", about
 * members the server was certain to reject: measured on PREDEV 2026-10-30,
 * 30,020 of the 30,057 members in the view were in that state - 30,000 seeded
 * fakes and 20 deleted accounts - every one of them reading "Active", with
 * nothing beside it to disagree.
 *
 * The verdict is the SERVER'S, never this component's. The panel renders what
 * the create's own predicate said; it never derives matchability, because a
 * second opinion in the panel could disagree with the create, which is this
 * same defect pointing the other way. A verdict the server did not give renders
 * as "not known" - never as a pass.
 */
import { describeMatchPoolTier, type MatchPoolTier } from '@/lib/match-pool-tier';
import { describeMatchBlock } from '@/lib/match-eligibility';

/** What the screen knows about one entered id. */
export type PoolTierLookup =
  /** No id typed yet, or not a uuid — nothing to say. */
  | { state: 'idle' }
  | { state: 'loading' }
  | {
      state: 'known';
      tier: MatchPoolTier;
      unopened_streak: number;
      /**
       * The create path's own verdict on this member. `null` / absent means the
       * server did not answer (an older API, mid-deploy) and is NEVER a pass.
       */
      matchable?: boolean | null;
      /** The machine reason behind `matchable: false`, straight from the server. */
      blocked_reason?: string | null;
    }
  /** A well-formed uuid that matches no member (the view has a row per member). */
  | { state: 'missing' }
  /** The lookup itself failed. Says so; never reads as 'active'. */
  | { state: 'failed' };

export function PoolTierNote({ lookup }: { lookup: PoolTierLookup }) {
  if (lookup.state === 'idle') return null;

  if (lookup.state === 'loading') {
    return <span className="text-xs text-muted-foreground">Match pool: checking…</span>;
  }

  if (lookup.state === 'missing') {
    return (
      <span className="text-xs text-amber-600 dark:text-amber-400">
        Match pool: no member has this ID.
      </span>
    );
  }

  if (lookup.state === 'failed') {
    return (
      <span className="text-xs text-muted-foreground">
        Match pool: not known — the lookup failed. This does not block the create.
      </span>
    );
  }

  const held = lookup.tier !== 'active';
  return (
    <span
      className={`text-xs ${
        held ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
      }`}
    >
      Match pool: {describeMatchPoolTier(lookup.tier, lookup.unopened_streak)}
      {held && (
        <span className="mt-0.5 block text-muted-foreground">
          An admin-created match ignores the tier on purpose. This is for your
          information; it does not block the create.
        </span>
      )}
      <MatchabilityLine
        matchable={lookup.matchable ?? null}
        reason={lookup.blocked_reason ?? null}
      />
    </span>
  );
}

/**
 * Whether the create will accept this member, as the server decided.
 *
 * It is DELIBERATELY a separate line with its own colour: the tier line says it
 * does not block, this one can say the create refuses, and an operator must not
 * read the two as one statement. Nothing here disables a control - the server
 * is the one authority on the refusal, and the operator can always make it
 * speak. A disabled button would hide the reason and could also sit on a stale
 * verdict after the member is fixed in another tab.
 */
function MatchabilityLine({
  matchable,
  reason,
}: {
  matchable: boolean | null;
  reason: string | null;
}) {
  if (matchable === false) {
    return (
      <span className="mt-0.5 block text-destructive">
        Matching: this member cannot be matched: {describeMatchBlock(reason)}. The
        create will refuse.
      </span>
    );
  }
  if (matchable === true) {
    return (
      <span className="mt-0.5 block text-muted-foreground">
        Matching: this member can be matched.
      </span>
    );
  }
  return (
    <span className="mt-0.5 block text-muted-foreground">
      Matching: not known. The create decides.
    </span>
  );
}
